import { useState, Fragment, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { styled, Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { Typography, Dialog, Divider } from '@mui/material'
import DialogControl from '../../control/Dialog'
import CoreHeader from '../map/CoreHeader'
import CoreFooter from '../map/CoreFooter'
import useLogin from '../../hooks/verify/useLogin'
import lottie from 'lottie-web'
import { Cookies } from 'react-cookie-consent'
import { useHistory } from 'react-router-dom'
import { decryptAES } from '../../util/cipher'
import useRelogin from '../../hooks/useRelogin'

const BgImage = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/bg_blue_dark.png`
const BgLoginImage = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/bg_login_no_logo.png`
const LogoImage = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/logo_main.png`

const Landing = () => {
    const { onClickOk, openDialogOk, alertMessageOk, openDialogError, alertMessageError } = useLogin()
    const [openDialog, setOpenDialog] = useState(true)
    const { t } = useTranslation()
    const classes = useStyles()
    const size = 150
    const animRef = useRef<any>(null)
    const history = useHistory()
    const { callServiceLogin2FA } = useRelogin()

    useEffect(() => {
        const search = globalThis.location.search
        const params = new URLSearchParams(search)
        const result = params.get('result')
        const state = params.get('state')
        const email = params.get('email')
        const cookieValidateToken = Cookies.get(process.env.REACT_APP_TOKEN_VALIDATE)
        const validateToken = cookieValidateToken ? JSON.parse(decryptAES(cookieValidateToken)) : null

        //console.log('validateToken ::', validateToken) // debug 2FA localhost
        if (
            cookieValidateToken &&
            validateToken &&
            state == validateToken.state &&
            email == validateToken.email &&
            result == 'success'
        ) {
            const param = {
                verifyToken: validateToken.verifyToken,
                email: validateToken.email,
                state: validateToken.state,
            }

            callServiceLogin2FA(param, false, validateToken.isRememberLogin)
            document.cookie = `${process.env.REACT_APP_TOKEN_VALIDATE}=; path=/; max-age=0`
        } else {
            history.push('/login?result=error')
        }
    }, [])

    const setContainerRef = useCallback((node: HTMLDivElement | null) => {
        if (animRef.current) {
            try {
                animRef.current.destroy()
            } catch (err) {
                console.log(err)
            }
            animRef.current = null
        }

        if (node) {
            const path = `${process.env.REACT_APP_IMAGE_PATH}/assets/loading/primary.json`
            animRef.current = lottie.loadAnimation({
                container: node,
                renderer: 'svg',
                loop: true,
                autoplay: true,
                path,
            })
        }
    }, [])

    return (
        <Fragment>
            <Dialog
                fullScreen
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false)
                }}
                classes={{ paper: classes.drawerPaper }}
            >
                <CoreHeader />

                <StyledContainer>
                    <StyledBox>
                        <StyledBoxLeft>
                            <img src={LogoImage} className={classes.logo} />
                        </StyledBoxLeft>
                        <StyledBoxRight>
                            <StyledDivForm>
                                <StyledDivTitle>
                                    <StyledTypoTitle>{t('LABEL.LOGIN.TITLE')}</StyledTypoTitle>
                                    <StyledTypoSubTitle>
                                        {'ยินดีต้อนรับเข้าสู่ระบบ DTT Service Area'}
                                    </StyledTypoSubTitle>
                                    <Divider />
                                </StyledDivTitle>
                                <StyledBoxLoading>
                                    <div ref={setContainerRef} style={{ height: size, width: size }}></div>
                                </StyledBoxLoading>
                            </StyledDivForm>
                        </StyledBoxRight>
                    </StyledBox>
                </StyledContainer>

                <StyledFooter>
                    <CoreFooter />
                </StyledFooter>
            </Dialog>
            <DialogControl
                type="ok"
                title={t('ALERT.TITLE.NOTIFY')}
                content={alertMessageOk}
                isOpen={openDialogOk}
                onClickOk={onClickOk}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
            <DialogControl
                type="ok"
                severity="error"
                title={t('ALERT.TITLE.ERROR')}
                content={alertMessageError}
                isOpen={openDialogError}
                onClickOk={onClickOk}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
        </Fragment>
    )
}

export default Landing

const useStyles = makeStyles((theme: Theme) => ({
    drawerPaper: {
        backgroundImage: `url(${BgImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%',
    },
    container: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    divRememberForgot: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
        },
    },
    checkbox: {
        color: `${theme.palette.primary.main} !important`,
        padding: '5px !important',
        fontSize: '14px !important',
        marginLeft: '5px !important',
    },
    checkboxChecked: {
        color: `${theme.palette.primary.main} !important`,
        padding: '5px !important',
        marginLeft: '5px !important',
    },
    divButtonGroup: {
        display: 'flex',
        justifyContent: 'center',
    },
    divButton: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginLeft: theme.spacing(1),
    },
    button: {
        width: '100%',
        minHeight: '37px',
        fontSize: '16px !important',
        boxShadow: `${theme.palette.getContrastText('shadow-bottom-blue')} !important`,
    },
    logo: {
        height: 'auto',
        position: 'relative',
        width: 300,
        top: 40,
        [theme.breakpoints.down('lg')]: {
            width: 240,
            top: 20,
        },
    },
    innerLoading: {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
    },
}))

const StyledFooter = styled('div')(({ theme }) => ({
    position: 'fixed',
    bottom: 0,
    width: '100%',
}))

const StyledContainer = styled('div')(({ theme }) => ({
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    width: 1120,
    marginLeft: 'auto',
    marginRight: 'auto',
    [theme.breakpoints.down('lg')]: {
        width: 860,

        padding: '8px 64px',
    },
    [theme.breakpoints.down('md')]: {
        width: 'auto',
        marginLeft: 0,
        marginRight: 0,
        padding: '6px 32px',
    },
    [theme.breakpoints.down('sm')]: {
        padding: '4px 20px',
    },
}))

const StyledBox = styled('div')(({ theme }) => ({
    width: '100%',
    height: '80%',
    display: 'flex',
    justifyContent: 'space-between',
    borderRadius: '5px',
    boxShadow: theme.palette.getContrastText('shadow-box-blue'),
}))

const StyledBoxLeft = styled('div')(({ theme }) => ({
    width: 480,
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    backgroundImage: `url(${BgLoginImage})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% 100%',
    borderRadius: '5px 0px 0px 5px',
    [theme.breakpoints.down('lg')]: {
        width: 500,
    },
    [theme.breakpoints.down('md')]: {
        display: 'none',
    },
}))

const StyledBoxRight = styled('div')(({ theme }) => ({
    width: 640,
    height: '100%',
    borderRadius: '5px',
    backgroundColor: theme.palette.getContrastText('bg-login-right'),
    [theme.breakpoints.down('lg')]: {
        width: 540,
    },
    [theme.breakpoints.down('md')]: {
        width: 'auto',
        flexGrow: 1,
    },
    [theme.breakpoints.down('sm')]: {
        overflow: 'auto',
    },
}))

const StyledDivForm = styled('div')(({ theme }) => ({
    padding: '20px 50px',
    [theme.breakpoints.down('sm')]: {
        padding: '10px 30px',
    },
}))

const StyledDivTitle = styled('div')(({ theme }) => ({
    padding: '20px 0px',
}))

const StyledTypoTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.dark,
    fontWeight: 'bold',
    fontSize: '20px',
}))

const StyledTypoSubTitle = styled(Typography)(({ theme }) => ({
    fontSize: '14px',
    margin: '10px 0px',
}))

const StyledBoxLoading = styled('div')(({ theme }) => ({
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
}))
